import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type MetricSeriesSpec,
    type UseMetricChartResult,
    useMetricChart,
} from '@/components/charts/generic';
import type { EnrichedBodyWoundsData, EnrichedDailyBodyWoundsData } from '@/domain/welfare/types';
import { buildWoundGraphSpec } from '@/routes/Client/Cage/pages/Health/HealthWounds/HealthWoundsBodyGraphs/woundGraphSpec';
import {
    formatNumberByLocale,
    formatPercentageByLocale,
    type Locale,
    parseLocale,
} from '@/utils/localeFormatters';

export interface WelfareWoundSeriesConfig {
    label: string;
    color: string;
    getValue: (day: EnrichedDailyBodyWoundsData) => number | null | undefined;
}

export interface UseWelfareWoundGraphChartParams {
    bodyWounds: EnrichedBodyWoundsData;
    woundSeries: WelfareWoundSeriesConfig[];
}

/** The footer's words, already translated. */
export interface WoundFooterLabels {
    total: string;
    of: string;
    fish: string;
}

/**
 * `Total: 11.7% of 1,217 fish`: the wound shares on screen added up for the hovered day, and
 * how many fish were counted that day. Temperature, on the right axis, is not a share.
 */
export function woundTotalFooter(
    days: EnrichedDailyBodyWoundsData[],
    labels: WoundFooterLabels,
    locale: Locale
): (index: number, visibleSeries: readonly MetricSeriesSpec[]) => string[] {
    return (index: number, visibleSeries: readonly MetricSeriesSpec[]): string[] => {
        const day = days[index];
        if (!day) return [];
        const sum = visibleSeries
            .filter((series) => series.axis !== 'right')
            .reduce((total, series) => {
                const value = series.data.find((point) => point.day === day.day)?.value;
                return value == null || Number.isNaN(value) ? total : total + value;
            }, 0);
        const fish = day.total != null && Number.isFinite(day.total) ? day.total : 0;
        return [
            `${labels.total}: ${formatPercentageByLocale(sum, locale, 1)} ${labels.of} ${formatNumberByLocale(fish, locale, 0)} ${labels.fish}`,
        ];
    };
}

/**
 * A welfare wound graph (Bioscope 1.0 body wounds) as a chart: the wound series in percent,
 * the sea temperature behind them, and the day's total in the tooltip footer.
 */
export function useWelfareWoundGraphChart(
    params: UseWelfareWoundGraphChartParams
): UseMetricChartResult<'line'> {
    const { bodyWounds, woundSeries } = params;
    const { t, i18n } = useTranslation();

    const spec = useMemo(() => {
        const locale = parseLocale(i18n.language);
        const { days } = bodyWounds;
        return buildWoundGraphSpec({
            days: days.map((day) => day.day),
            series: woundSeries.map((series, index) => ({
                key: `wound:${index}`,
                label: series.label,
                color: series.color,
                values: days.map(series.getValue),
            })),
            temperature: days.map((day) => day.temperature),
            labels: {
                axis: t('common.labels.wounds'),
                temperature: t('common.labels.temperature'),
            },
            locale,
            tooltipFooterLines: woundTotalFooter(
                days,
                {
                    total: t('common.labels.total'),
                    of: t('cageWelfare.wounds.of'),
                    fish: t('cageWelfare.wounds.fish'),
                },
                locale
            ),
        });
    }, [bodyWounds, woundSeries, t, i18n.language]);

    return useMetricChart(spec);
}
